import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS } from '@/constants';
import { TASK_STATUS } from '@/types';

import type { TaskDetail } from '@/types';

interface ViewActionDialogProps {
  task: TaskDetail;
  onClose: () => void;
  onAccept?: (() => void) | undefined;
  onStart?: (() => void) | undefined;
  onCancel?: (() => void) | undefined;
  isLoading?: boolean | undefined;
}

/**
 * ViewAction task detail popup — matches Xamarin ViewActionPage.xaml (tablet) layout.
 *
 * Xamarin layout (ViewActionPage.xaml):
 *  - Frame: Margin="{OnIdiom Default='80,50', Phone='10,50'}", Padding=10, CornerRadius=8, bg=White
 *  - Grid RowDefinitions="Auto, *, Auto":
 *    - Row 0: Centered "Details" label + icon (MoveDetails/AuditDetails, 25px)
 *    - Row 1: Bordered content frame (ViewMoveBorderColor #E8CDAF / ViewAuditBorderColor #7599BD)
 *      - Sub-Row 0: Colored header (Move=#E38C25, Audit=#273F94, Padding="10,20")
 *        - Col 0: Task icon (25px) + type label (white, Roboto bold)
 *        - Col 1: Task description (FormattedString, PoppinsRegular/PoppinsBold, white)
 *      - Sub-Row 1: Content area (ViewMoveTaskBackgroundColor #FFF8F0 / ViewAuditTaskBackgroundColor #F1F5FF)
 *        - FlexLayout wrapping detail fields (250px each)
 *        - TrailerComments frame (TaskNotesBorderColor #415B6D, CornerRadius=10, HeightRequest=80)
 *        - TaskComments frame (same styling)
 *    - Row 2: Close button (transparent bg, colored border, centered)
 */
export function ViewActionDialog({ task, onClose }: ViewActionDialogProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const isMove = task.TaskType === 'Move';
  const isCompleted = task.Status === TASK_STATUS.COMPLETED;
  const isInProgress = task.Status === TASK_STATUS.IN_PROGRESS;

  // Xamarin colors — ViewActionPage.xaml + App.xaml
  const headerBg = isMove ? '#E38C25' : '#273F94'; // MoveTaskBackgroundColor / AuditTaskBackgroundColor
  const frameBorderColor = isMove ? '#E8CDAF' : '#7599BD'; // ViewMoveBorderColor / ViewAuditBorderColor
  const contentBg = isMove ? '#FFF8F0' : '#F1F5FF'; // ViewMoveTaskBackgroundColor / ViewAuditTaskBackgroundColor
  const detailsIcon = isMove ? IMAGE_CONSTANTS.moveDetails : IMAGE_CONSTANTS.auditDetails;
  const taskIcon = isMove ? IMAGE_CONSTANTS.moveTask : IMAGE_CONSTANTS.auditTask;

  // Build detail fields matching Xamarin ViewActionViewModel.SetViewActionList()
  const detailFields: { title: string; value: string }[] = [];

  detailFields.push({ title: t('dashboard.taskId'), value: task.TaskId });
  if (task.TrailerName) {
    detailFields.push({ title: t('dashboard.trailerNumber'), value: task.TrailerName });
  }
  if (task.CarrierCompanyName) {
    detailFields.push({ title: t('dashboard.carrierName'), value: task.CarrierCompanyName });
  }

  if (isMove) {
    if (task.MoveSourceName) {
      detailFields.push({
        title: t('dashboard.sourceLocation'),
        value: `${task.MoveSourceZone} - ${task.MoveSourceName} (${task.MoveSourceType})`,
      });
    }
    // Xamarin SetViewActionList: Complete or InProgress → show full destination, else "*****"
    if (isCompleted || isInProgress) {
      if (task.MoveDestinationName) {
        detailFields.push({
          title: t('dashboard.destinationLocation'),
          value: `${task.MoveDestinationZone} - ${task.MoveDestinationName} (${task.MoveDestinationType})`,
        });
      }
    } else {
      detailFields.push({
        title: t('dashboard.destinationLocation'),
        value: '*****',
      });
    }
  } else {
    if (task.AuditLocationName) {
      detailFields.push({
        title: t('dashboard.sourceLocation'),
        value: `${task.AuditLocationZone} - ${task.AuditLocationName} (${task.AuditLocationType})`,
      });
    }
  }

  if (isCompleted) {
    if (task.TaskStartDateTime) {
      detailFields.push({ title: t('dashboard.taskStartTime'), value: task.TaskStartDateTime });
    }
    if (task.TaskEndDateTime) {
      detailFields.push({ title: t('dashboard.taskEndTime'), value: task.TaskEndDateTime });
    }
  }

  if (task.ExceptionText) {
    detailFields.push({ title: t('dashboard.exceptionNote'), value: task.ExceptionText });
  }
  if (task.AlternateCarrier) {
    detailFields.push({ title: t('dashboard.altCarrierName'), value: task.AlternateCarrier });
  }
  if (task.AlternateTrailerName) {
    detailFields.push({ title: t('dashboard.altTrailerNumber'), value: task.AlternateTrailerName });
  }
  if (task.AlternateDestinationZone) {
    detailFields.push({
      title: t('dashboard.altLocation'),
      value: `${task.AlternateDestinationZone} - ${task.AlternateDestinationName}`,
    });
  }

  // Xamarin MoveTaskTypeToDestinationNameConverter: shows "DL****" for non-completed, actual dest for completed
  const headerDest = isCompleted ? task.MoveDestinationName : 'DL****';

  return (
    /* Xamarin: PopupPage overlays full screen with semi-transparent bg */
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center animate-in fade-in duration-200 touch-none">
      {/* Xamarin ViewActionPage: Frame Margin="{OnIdiom Default='80,50', Phone='10,50'}",
          Padding=10, CornerRadius=8, bg=White */}
      <div className="flex flex-col mx-[10px] my-[50px] md:mx-20 w-full max-h-[calc(100dvh-100px)] bg-white rounded-lg p-[10px] overflow-hidden animate-in zoom-in-95 duration-200 touch-auto">
        {/* ── Row 0: "Details" title with icon ──
            Xamarin: StackLayout Horizontal, HorizontalOptions=Center, VerticalOptions=Center
            Icon: MoveDetails/AuditDetails 25px
            Label: "Details", PoppinsRegular, 13px (phone) / 18px (default) */}
        <div className="flex items-center justify-center gap-2 shrink-0">
          <img src={detailsIcon} alt="" className="h-[25px] w-[25px] object-contain" />
          <span className="text-[13px] md:text-lg font-poppins font-normal">
            {t('common.details')}
          </span>
        </div>

        {/* ── Row 1: Bordered content frame ──
            Xamarin: Frame Margin="0,10", CornerRadius=8, HasShadow=False,
            BorderColor=ViewMoveBorderColor #E8CDAF / ViewAuditBorderColor #7599BD */}
        <div
          className="flex flex-col flex-1 min-h-0 my-[10px] rounded-lg overflow-hidden"
          style={{ border: `1px solid ${frameBorderColor}` }}
        >
          {/* Sub-Row 0: Colored header bar
              Xamarin: Frame HeightRequest=30, HasShadow=False, Padding="10,20"
              bg=MoveTaskBackgroundColor #E38C25 / AuditTaskBackgroundColor #273F94
              Grid: Col 0 (Auto) = icon+type, Col 1 (*) = description, Col 2 (Auto) = empty */}
          <div className="shrink-0 px-[10px] py-[20px]" style={{ backgroundColor: headerBg }}>
            <div className="flex items-center gap-[15px]">
              {/* Col 0: Task icon + type label (vertical stack)
                  Xamarin: SvgCachedImage 25px + Label Roboto bold, white, 14px phone / 16px default */}
              <div className="flex flex-col items-center shrink-0" style={{ marginTop: '-10px' }}>
                <img
                  src={taskIcon}
                  alt={task.TaskType}
                  className="h-[25px] w-[25px] object-contain"
                />
                <span className="text-white text-sm md:text-base font-sans font-bold text-center">
                  {task.TaskType}
                </span>
              </div>

              {/* Col 1: Task description (FormattedString with bold/regular spans)
                  Xamarin: PoppinsRegular/PoppinsBold, white, 11px phone / 16px default */}
              <div className="flex-1 min-w-0">
                {isMove ? (
                  <p className="text-[11px] md:text-base text-white font-poppins font-normal leading-snug">
                    <span>{t('dashboard.moveDescription')} </span>
                    <span className="font-bold">{task.TrailerName}</span>
                    <span> {t('dashboard.from')} </span>
                    <span className="font-bold">{task.MoveSourceName}</span>
                    <span> {t('dashboard.to')} </span>
                    <span className="font-bold">{headerDest}</span>
                  </p>
                ) : task.TrailerName ? (
                  <p className="text-[11px] md:text-base text-white font-poppins font-normal leading-snug">
                    <span className="font-bold">{t('dashboard.auditDescriptionWithTrailer')}</span>
                    <span className="font-bold">{task.TrailerName}</span>
                    <span> {t('dashboard.in')} </span>
                    <span className="font-bold">{task.AuditLocationName}</span>
                  </p>
                ) : (
                  <p className="text-[11px] md:text-base text-white font-poppins font-normal leading-snug">
                    <span>{t('dashboard.auditDescriptionWithoutTrailer')}</span>
                    <span className="font-bold">{task.AuditLocationName}</span>
                    <span>{t('dashboard.emptyOrNot')}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sub-Row 1: Content area with light background
              Xamarin: Frame bg=ViewMoveTaskBackgroundColor #FFF8F0 / ViewAuditTaskBackgroundColor #F1F5FF,
              Padding=10, CornerRadius=0, Margin="0,-5" */}
          <div
            className="flex-1 overflow-y-auto min-h-0 p-[10px] overscroll-contain"
            style={{ backgroundColor: contentBg }}
          >
            {/* Xamarin: ScrollView > Grid RowDefinitions="*, Auto, Auto"
                Row 0 (*): FlexLayout detail fields at top
                Row 1 (Auto): Trailer Comments at bottom
                Row 2 (Auto): Task Comments at bottom */}
            <div className="flex flex-col min-h-full">
              {/* Row 0: Detail fields — Xamarin: FlexLayout Wrap, Direction=Row, VerticalOptions=Start
                  Each item: Grid WidthRequest=250, Margin=10
                  Title: ViewTitleLabelStyle — Roboto, 12px phone / 18px default, BlackLabelColor #000000
                  Value: ViewItemLabelStyle — Roboto, 10px phone / 14px default, ViewItemLabelColor #4D4D4D */}
              <div className="flex flex-wrap">
                {detailFields.map((field) => (
                  <div key={field.title} className="w-full sm:w-[250px] m-[10px]">
                    <p className="text-xs md:text-lg font-sans font-normal text-black">
                      {field.title}
                    </p>
                    <p
                      className="text-[10px] md:text-sm font-sans font-normal"
                      style={{ color: '#4D4D4D' }}
                    >
                      {field.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Spacer — pushes comments to the bottom of the content area */}
              <div className="flex-1" />

              {/* Row 1: Trailer Comments — Xamarin ViewActionPage:
                  Frame HeightRequest=80, BorderColor=TaskNotesBorderColor #415B6D,
                  CornerRadius=10, Padding=2 (tablet), HasShadow=False
                  Label inside: "Trailer Comments :" (Roboto 12px/18px, BlackLabelColor #000000)
                  + value text (ViewItemLabelStyle) */}
              {task.TrailerComments && (
                <div
                  className="mx-[10px] mt-[10px] min-h-[80px] rounded-[10px] p-[2px]"
                  style={{ border: '1px solid #415B6D' }}
                >
                  <div className="p-[10px]">
                    <p className="text-xs md:text-lg font-sans font-normal">
                      <span className="text-black">{t('dashboard.trailerComments')}:</span>{' '}
                      <span style={{ color: '#4D4D4D' }}>{task.TrailerComments}</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Row 2: Task Comments — Xamarin: same styling as TrailerComments, Margin="0,10,0,0" */}
              {task.TaskComments && (
                <div
                  className="mx-[10px] mt-[10px] min-h-[80px] rounded-[10px] p-[2px]"
                  style={{ border: '1px solid #415B6D' }}
                >
                  <div className="p-[10px]">
                    <p className="text-xs md:text-lg font-sans font-normal">
                      <span className="text-black">{t('dashboard.taskComments')}:</span>{' '}
                      <span style={{ color: '#4D4D4D' }}>{task.TaskComments}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Row 2: Close button ──
            Xamarin: Button BackgroundColor=Transparent, CornerRadius=8, Padding=10,
            Text="Close", BorderWidth=1, HorizontalOptions=CenterAndExpand
            BorderColor=ViewMoveBorderColor #E8CDAF / ViewAuditBorderColor #7599BD */}
        <div className="flex justify-center shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-[10px] py-[10px] bg-transparent font-poppins font-normal text-sm md:text-base"
            style={{ border: `1px solid ${frameBorderColor}` }}
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
